import {
  excludedFollowDidsForAccount,
  listCampaignFollowActions,
  listDueAutoFollowCampaigns,
  upsertCampaign,
  upsertScheduledAction,
  type AutoFollowCampaignRow,
  type ScheduledActionRow,
} from '../cache/db'
import { useAccounts } from '../store/accounts'
import { pickCandidatesForCampaign } from '../api/actorDiscovery'

function newActionId(): string {
  return `a_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

/** Hard floor — even if the user lowers cadence below this, the
 *  runner refuses to queue faster. Bsky's official rate cap is
 *  5000 writes/hour per account; 10s = 360/hour, which still
 *  leaves a 13× safety buffer even with several campaigns running. */
const ABSOLUTE_FLOOR_MS = 10_000

/** Hard cap on follows queued per scheduler tick — even at very
 *  short cadences, we never enqueue more than this in one go.
 *  Keeps the IndexedDB writes / candidate-search calls bounded
 *  and lets scheduledSoFar grow visibly rather than spiking. */
const QUEUE_PER_TICK_MAX = 10

/** The scheduler ticks every 30s. To honour a 10s cadence we need
 *  to queue ~3 follows per tick (3 × 10s = 30s of runway). The
 *  caller multiplies cadenceMs out per-action, so the actions
 *  themselves still fire at the requested cadence — this just
 *  controls how many we ENQUEUE each tick. */
const SCHEDULER_TICK_MS = 30_000

/**
 * Walk every due running campaign and, for each one, queue up to
 * QUEUE_PER_TICK new follow actions whose runAt respects cadenceMs.
 * Auto-marks status='done' when scheduledSoFar reaches totalTarget.
 *
 * Errors per campaign are isolated — one bad search won't stall
 * the rest of the tick.
 */
export async function tickAutoFollowCampaigns(now: number): Promise<void> {
  const due = await listDueAutoFollowCampaigns(now)
  const accounts = useAccounts.getState().accounts
  for (const camp of due) {
    try {
      await tickOne(camp, now, accounts)
    } catch (e) {
      console.error('[autoFollow] campaign failed', camp.id, e)
    }
  }
}

async function tickOne(
  camp: AutoFollowCampaignRow,
  now: number,
  accounts: { id: string; did: string }[],
): Promise<void> {
  // Reconcile counters from actions tagged with this campaignId.
  // CRITICAL: only ever RAISE the counters — never lower them.
  // Pre-v0.21.2 actions don't have campaignId so own.length is 0
  // for legacy campaigns. If we blindly wrote that back we'd nuke
  // the stored progress every tick.
  const own = await listCampaignFollowActions(camp.id)
  const succeededSoFar = Math.max(
    camp.succeededSoFar,
    own.filter((a) => a.status === 'done').length,
  )
  const failedSoFar = Math.max(
    camp.failedSoFar,
    own.filter((a) => a.status === 'failed').length,
  )
  const scheduledSoFar = Math.max(camp.scheduledSoFar, own.length)
  if (
    scheduledSoFar !== camp.scheduledSoFar ||
    succeededSoFar !== camp.succeededSoFar ||
    failedSoFar !== camp.failedSoFar
  ) {
    camp = {
      ...camp,
      scheduledSoFar,
      succeededSoFar,
      failedSoFar,
      updatedAt: now,
    }
  }

  const remaining = camp.totalTarget - camp.scheduledSoFar
  if (remaining <= 0) {
    await upsertCampaign({
      ...camp,
      status: 'done',
      lastTickAt: now,
      lastTickMessage: 'target reached → marked done',
      updatedAt: now,
    })
    return
  }

  const account = accounts.find((a) => a.did === camp.accountDid)
  if (!account) {
    // Can't enqueue follows without the owning account signed in
    // on this device; cool off for a minute and try again.
    await upsertCampaign({
      ...camp,
      nextTickAt: now + 60_000,
      lastTickAt: now,
      lastTickMessage: 'campaign account not signed in on this device',
      updatedAt: now,
    })
    return
  }

  const cadenceMs = Math.max(ABSOLUTE_FLOOR_MS, camp.cadenceMs)
  const jitterMs = Math.max(0, camp.jitterMs ?? 0)
  // Queue enough follows this tick to keep the campaign saturated
  // until the next tick fires — runAt spacing still enforces the
  // cadence, so the actions themselves don't burst.
  const perTick = Math.max(
    1,
    Math.min(QUEUE_PER_TICK_MAX, Math.ceil(SCHEDULER_TICK_MS / cadenceMs)),
  )
  const want = Math.min(remaining, perTick)

  // Dedupe against EVERY DID we've ever attempted to follow on
  // this account — not just currently-pending ones. Otherwise as
  // the runner finishes follows the actors fall back into the
  // discovery pool and we'd re-queue them, which bsky rejects as
  // duplicates and the campaign appears to stall.
  const excludeDids = await excludedFollowDidsForAccount(camp.accountDid)
  let candidates: Awaited<ReturnType<typeof pickCandidatesForCampaign>> = []
  let searchErr: string | undefined
  try {
    candidates = await pickCandidatesForCampaign({
      accountId: account.id,
      tags: camp.tags,
      denyTags: camp.denyTags,
      want,
      excludeDids,
    })
  } catch (e) {
    searchErr = e instanceof Error ? e.message : String(e)
  }

  if (candidates.length === 0) {
    // No fresh candidates this tick — back off for a full cadence
    // window before searching again so we don't hammer the AppView.
    const msg = searchErr
      ? `search error: ${searchErr}`
      : `0 fresh candidates — every tag-match is already in this account's follow history (${excludeDids.size} excluded). try different tags or hit 🧹 reset progress.`
    await upsertCampaign({
      ...camp,
      nextTickAt: now + cadenceMs + Math.floor(Math.random() * jitterMs),
      lastTickAt: now,
      lastTickMessage: msg,
      updatedAt: now,
    })
    return
  }

  let nextRunAt = now
  let queued = 0
  for (const a of candidates) {
    nextRunAt += cadenceMs + Math.floor(Math.random() * jitterMs)
    const action: ScheduledActionRow = {
      id: newActionId(),
      accountDid: camp.accountDid,
      kind: 'follow',
      subject: a.did,
      subjectCid: null,
      runAt: nextRunAt,
      label: `auto-follow @${a.handle}`,
      status: 'pending',
      campaignId: camp.id,
      createdAt: now,
      updatedAt: now,
    }
    await upsertScheduledAction(action)
    queued++
  }

  const newScheduled = camp.scheduledSoFar + queued
  const done = newScheduled >= camp.totalTarget
  await upsertCampaign({
    ...camp,
    scheduledSoFar: newScheduled,
    status: done ? 'done' : camp.status,
    // Wait at least until the LAST follow we just queued has been
    // executed before we tick this campaign again.
    nextTickAt: nextRunAt,
    lastTickAt: now,
    lastTickMessage: `queued ${queued} follow${queued === 1 ? '' : 's'}${
      done ? ' (target reached)' : ''
    }`,
    updatedAt: now,
  })
}
